/* Плавающий ассистент — веб-двойник Telegram-бота. ТЗ 6.9, 9.1, 9.4.
   Любой ввод сначала показывается карточкой-подтверждением, запись — только после «Подтвердить». */

import { rub } from "@mera/shared";
import { Button, Sheet, Toolbar } from "konsta/react";
import React, { useState } from "react";
import { useLocation, useNavigate } from "../lib/router.jsx";

import { api } from "../lib/api.js";
import { useApp } from "../lib/store.jsx";
import { haptic } from "../lib/tg.js";
import { Ion } from "./icons.jsx";
import { Hint } from "./ui.jsx";

const FOCUS_BY_PATH = {
  "/": "Обзор",
  "/finance": "Финучет",
  "/crm": "CRM",
  "/alerts": "Алерты",
  "/businesses": "Бизнесы",
  "/reports": "Отчёты",
};

const INTENT_LABEL = {
  operation: "Операция",
  task: "Задача",
  note: "Заметка",
  attachment: "Вложение",
  deal_stage: "Смена этапа сделки",
  acc_stage: "Смена стадии аккаунтинга",
  schedule: "Повторяющийся платёж",
  new_deal: "Новая сделка",
  report: "Ежедневный отчёт",
};

export default function Assistant() {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [answer, setAnswer] = useState(null);
  const [err, setErr] = useState(null);
  const { pathname } = useLocation();
  const { reloadAlerts, assistantOpen: open, closeAssistant } = useApp();
  const navigate = useNavigate();

  const focus =
    FOCUS_BY_PATH[pathname] ||
    (pathname.startsWith("/deal/") ? "Карточка сделки" : pathname.startsWith("/project/") ? "Карточка проекта" : null);

  const reset = () => {
    setText("");
    setDraft(null);
    setAnswer(null);
    setErr(null);
  };

  const close = () => {
    closeAssistant();
    setTimeout(reset, 250);
  };

  const ingest = async () => {
    if (!text.trim()) return;
    setBusy(true);
    setErr(null);
    setAnswer(null);
    try {
      const res = await api.post("/bot/ingest", { text: text.trim(), focus });
      if (res.intent === "question") {
        setAnswer(res.answer);
        setDraft(null);
      } else if (res.missing?.length) {
        /* Не хватает обязательного — карточку не показываем: половина полей в ней
           была бы догадкой. Текст остаётся в поле, его дописывают и разбирают снова */
        setErr(`Не хватает: ${res.missing.join(", ")}.`);
        setDraft(null);
      } else {
        setDraft(res);
      }
      haptic.light();
    } catch (e) {
      haptic.error();
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const res = await api.post("/bot/commit", { draftId: draft.draftId });
      haptic.success();
      reloadAlerts();
      close();
      // Переходим туда, куда легла запись
      if (res.kind === "deal") navigate(`/deal/${res.id}`);
      else if (res.kind === "project") navigate(`/project/${res.id}`);
      else if (res.kind === "operation") navigate("/finance");
    } catch (e) {
      haptic.error();
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (draft?.draftId) await api.post("/bot/cancel", { draftId: draft.draftId });
    setDraft(null);
  };

  return (
    <>
      <Sheet opened={open} onBackdropClick={close} className="pb-safe w-full">
        <Toolbar top>
          <div className="flex-1 display text-[17px] font-semibold truncate pr-2">Ассистент</div>
          <Button clear onClick={close}>Закрыть</Button>
        </Toolbar>

        <div className="px-4 pt-1 pb-3">
          {focus ? (
            <div className="mb-2">
              <Hint>Контекст: {focus}</Hint>
            </div>
          ) : null}

          {/* Ввод */}
          {!draft && !answer ? (
            <>
              <textarea
                rows={3}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) ingest();
                }}
                placeholder="«+150000 оплата РЕТ», «задача по Атланту — сдать сайт до 15.07 на Илью», «дотягиваем до цели?»"
                className="w-full resize-none rounded-xl px-3 py-2.5 text-[15px] leading-snug"
                style={{ background: "var(--tg-section-bg)", color: "var(--tg-text)" }}
              />
              <Button large className="mt-2" onClick={ingest} disabled={busy || !text.trim()}>
                <Ion.paperPlane size={15} className="mr-1.5" />
                {busy ? "Разбираем…" : "Разобрать"}
              </Button>
              <div className="mt-2 text-center">
                <Hint>Запись появится только после подтверждения</Hint>
              </div>
            </>
          ) : null}

          {/* Ответ на вопрос по данным */}
          {answer ? (
            <>
              <div className="card-tg px-3.5 py-3 text-[14px] leading-snug rise">{answer}</div>
              <Button large className="mt-2.5" onClick={reset}>Спросить ещё</Button>
            </>
          ) : null}

          {/* Карточка-подтверждение */}
          {draft ? (
            <div className="rise">
              <div className="card-tg px-3.5 py-3">
                <div className="eyebrow px-0 pt-0 pb-1.5">
                  {INTENT_LABEL[draft.intent] || draft.intent}
                </div>
                <div className="text-[14px] leading-snug mb-2">{draft.draft.summary}</div>
                <DraftFields draft={draft.draft} names={draft.names} />
                {draft.draft.clarify ? (
                  <div
                    className="mt-2 rounded-lg px-3 py-2 text-[13px]"
                    style={{ background: "color-mix(in srgb, var(--fin-gold) 12%, transparent)", color: "var(--fin-gold)" }}
                  >
                    {draft.draft.clarify}
                  </div>
                ) : null}
              </div>

              <div className="mt-2.5 flex gap-2">
                <Button large className="flex-1" onClick={commit} disabled={busy}>
                  <Ion.checkmark size={16} className="mr-1.5" /> Подтвердить
                </Button>
                <Button large clear className="flex-1" onClick={cancel} disabled={busy}>
                  <Ion.close size={16} className="mr-1.5" /> Исправить
                </Button>
              </div>
            </div>
          ) : null}

          {err ? (
            <div className="mt-2 text-center text-[13px]" style={{ color: "var(--fin-red)" }}>
              {err}
            </div>
          ) : null}
        </div>
      </Sheet>
    </>
  );
}

const LABELS = {
  date: "Дата", biz: "Бизнес", acc: "Счёт", type: "Тип", act: "Деятельность", cat: "Категория",
  cp: "Контрагент", amount: "Сумма", proj: "Проект", doc: "Документ", note: "Описание",
  title: "Текст", who: "Ответственный", dueDate: "Срок", dueTime: "Время", remind: "Напоминание",
  text: "Текст", company: "Компания", funnel: "Воронка", tier: "Тир", status: "Этап",
  accStage: "Стадия", projectId: "Проект", dealId: "Сделка", day: "Число месяца",
  active: "Включено", kind: "Тип", name: "Файл", lpr: "ЛПР", src: "Источник",
  revenue: "Выручка", owner: "Ответственный", summary: "Сводка", tomorrow: "На завтра",
};

/* Бизнес и счёт в черновике лежат как id — показываем названия, которые сервер
   разобрал по ним: «dialogic-ai» человеку ничего не говорит */
function DraftFields({ draft, names }) {
  const section = draft[draft.intent];
  if (!section || typeof section !== "object") return null;
  const human = (k, v) => (k === "biz" && names?.biz) || (k === "acc" && names?.acc) || v;

  return (
    <div className="rounded-lg px-3 py-2" style={{ background: "var(--tg-secondary-bg)" }}>
      {Object.entries(section)
        .filter(([, v]) => v !== "" && v !== null && v !== undefined && v !== 0)
        .map(([k, v]) => (
          <div key={k} className="flex items-baseline justify-between gap-3 py-0.5">
            <span className="text-[12px] shrink-0" style={{ color: "var(--tg-hint)" }}>
              {LABELS[k] || k}
            </span>
            <span className="text-[13px] font-medium text-right min-w-0 truncate">
              {k === "amount" ? rub(v) : typeof v === "boolean" ? (v ? "да" : "нет") : Array.isArray(v) ? v.join(" · ") : String(human(k, v))}
            </span>
          </div>
        ))}
    </div>
  );
}
